import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/base/SEOSchema";
import { articles } from "@/mocks/articles";

const categoryNames: Record<string, string> = {
  hot: "热门推荐",
  movie: "最新电影",
  tv: "电视剧",
  variety: "综艺节目",
  anime: "动漫动画",
  doc: "纪录片",
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const article = useMemo(() => {
    return articles.find((a) => a.id === Number(id));
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-900 mb-2">{t("page_not_found")}</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
            {t("back_to_list")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryName = categoryNames[article.category] || article.category;
  const currentIndex = articles.findIndex((a) => a.id === article.id);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: {
            "@type": "Organization",
            name: "91影视看片下载",
          },
          publisher: {
            "@type": "Organization",
            name: "91影视看片下载",
          },
          image: article.image,
          url: `https://www.91yingshi.com/article/${article.id}`,
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.91yingshi.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: categoryName,
              item: `https://www.91yingshi.com/category/${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {/* Article Header */}
            <header className="mb-8">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000
                    ? `${(article.views / 10000).toFixed(1)}万阅读`
                    : `${article.views}阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line"></i>
                  {categoryName}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-[300px] md:h-[450px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 91影视看片下载`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Navigation */}
            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                {prevArticle ? (
                  <Link
                    to={`/article/${prevArticle.id}`}
                    className="flex-1 group p-4 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <div className="text-xs text-foreground-500 mb-1">{t("prev_article")}</div>
                    <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {prevArticle.title}
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1"></div>
                )}
                {nextArticle ? (
                  <Link
                    to={`/article/${nextArticle.id}`}
                    className="flex-1 group p-4 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer text-right"
                  >
                    <div className="text-xs text-foreground-500 mb-1">{t("next_article")}</div>
                    <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {nextArticle.title}
                    </div>
                  </Link>
                ) : (
                  <div className="flex-1"></div>
                )}
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative h-36 rounded-lg overflow-hidden mb-3">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <div className="text-xs text-foreground-400 mt-1">{related.date}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}